/**
 * Client-side & server-side utilities for working with unified API responses.
 *
 * This wrapper normalises relative URLs (e.g. `/api/foo`) into absolute URLs so
 * that it works correctly in:
 * - Browsers (where relative URLs are resolved against `window.location`)
 * - Node/SSR runtimes (Next.js / React Server Components), where `fetch`
 *   requires absolute URLs and will otherwise throw `TypeError: Failed to parse URL from /relative`.
 */

import { APP_URL } from '@/lib/env'
import type { ApiResponse } from './errors'

function resolveApiUrl(url: string): string {
  // Absolute URL: leave as-is.
  if (/^https?:\/\//i.test(url)) {
    return url
  }

  // Browser: resolve against the current origin so cookies / same-origin
  // behaviour are preserved.
  if (typeof window !== 'undefined' && window.location?.origin) {
    return new URL(url, window.location.origin).toString()
  }

  // Node / SSR (including Next RSC): fall back to the configured app URL.
  // This ensures `fetch` always sees an absolute URL and never throws on
  // relative paths like `/api/threads/...`.
  return new URL(url, APP_URL).toString()
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function withStatusDetails(details: unknown, status: number): unknown {
  if (isObject(details)) {
    return 'status' in details ? details : { status, ...details }
  }

  if (details === undefined) {
    return { status }
  }

  return { status, payload: details }
}

function legacyHttpErrorCode(status: number): string {
  switch (status) {
    case 400:
      return 'bad_request'
    case 401:
      return 'unauthorized'
    case 403:
      return 'forbidden'
    case 404:
      return 'not_found'
    case 409:
      return 'conflict'
    case 429:
      return 'rate_limited'
    case 503:
      return 'service_unavailable'
    default:
      return 'http_error'
  }
}

function legacyHttpErrorMessage(payload: unknown, response: Response): string {
  if (typeof payload === 'string' && payload.trim().length > 0) {
    return payload
  }

  if (isObject(payload)) {
    const nestedError = payload.error
    if (typeof nestedError === 'string' && nestedError.trim().length > 0) {
      return nestedError
    }
    if (isObject(nestedError) && typeof nestedError.message === 'string' && nestedError.message.trim().length > 0) {
      return nestedError.message
    }
    if (typeof payload.message === 'string' && payload.message.trim().length > 0) {
      return payload.message
    }
  }

  return response.statusText || `Request failed with status ${response.status}`
}

/**
 * Type-safe fetch wrapper that handles unified API responses
 *
 * @param url - The URL to fetch (relative or absolute)
 * @param init - Fetch init options
 * @param timeout - Request timeout in milliseconds (default: 10000ms / 10s)
 *
 * @example
 * const result = await apiClient<UserData>('/api/users/me')
 * if (result.success) {
 *   console.log(result.data.email)
 * } else {
 *   console.error(result.error.code, result.error.message)
 * }
 *
 * @example With custom timeout
 * const result = await apiClient<UserData>('/api/users/me', {}, 5000)
 *
 * @example Without timeout
 * const result = await apiClient<UserData>('/api/users/me', {}, undefined)
 */
export async function apiClient<T>(
  url: string,
  init?: RequestInit,
  timeout: number | undefined = 10000
): Promise<ApiResponse<T>> {
  let timeoutId: NodeJS.Timeout | undefined
  const controller = new AbortController()

  // Set up timeout if specified (skip if explicitly undefined)
  if (timeout !== undefined) {
    timeoutId = setTimeout(() => controller.abort(), timeout)
  }

  const resolvedUrl = resolveApiUrl(url)

  try {
    const response = await fetch(resolvedUrl, {
      ...init,
      signal: init?.signal ? AbortSignal.any([init.signal, controller.signal]) : controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    })

    const payload = await response.json().catch(() => null)

    if (!response.ok) {
      if (
        isObject(payload)
        && payload.success === false
        && isObject(payload.error)
      ) {
        const code = typeof payload.error.code === 'string'
          ? payload.error.code
          : legacyHttpErrorCode(response.status)
        const message = typeof payload.error.message === 'string'
          ? payload.error.message
          : legacyHttpErrorMessage(payload, response)

        return {
          success: false,
          error: {
            code,
            message,
            kind: typeof payload.error.kind === 'string' ? payload.error.kind : undefined,
            details: withStatusDetails(payload.error.details, response.status),
          },
        } as ApiResponse<T>
      }

      return {
        success: false,
        error: {
          code: legacyHttpErrorCode(response.status),
          message: legacyHttpErrorMessage(payload, response),
          details: withStatusDetails(payload, response.status),
        },
      } as ApiResponse<T>
    }

    return payload as ApiResponse<T>
  } catch (error) {
    // Convert AbortError to a structured API error
    if (error instanceof Error && error.name === 'AbortError') {
      return {
        success: false,
        error: {
          code: 'request_timeout',
          message: `Request timed out after ${timeout}ms`,
        },
      } as ApiResponse<T>
    }
    throw error
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
  }
}

/**
 * Helper to extract data from API response or throw on error
 * Useful when you want to use Promise chains without manual error checking
 * 
 * @example
 * try {
 *   const data = await apiClient('/api/users/me').then(unwrap)
 *   console.log(data.email)
 * } catch (error) {
 *   console.error('API error:', error)
 * }
 */
export function unwrap<T>(response: ApiResponse<T>): T {
  if (response.success) {
    return response.data
  }

  const error = new ApiClientError(
    response.error.code,
    response.error.message,
    response.error.details,
    response.error.kind
  )
  throw error
}

/**
 * Custom error class for API client errors
 * Preserves error code and details from the API response
 */
export class ApiClientError extends Error {
  public status?: number
  public kind?: string

  constructor(
    public code: string,
    message: string,
    public details?: unknown,
    kind?: string
  ) {
    // Build comprehensive error message with all context
    const fullMessage = details 
      ? `[${code}] ${message} (details: ${JSON.stringify(details)})`
      : `[${code}] ${message}`
    super(fullMessage)
    this.name = 'ApiClientError'
    if (isObject(details) && typeof details.status === 'number') {
      this.status = details.status
    }
    if (kind !== undefined) {
      this.kind = kind
    }
  }
}

/**
 * Type guard to check if response is successful
 * 
 * @example
 * const response = await apiClient<UserData>('/api/users/me')
 * if (isSuccess(response)) {
 *   // TypeScript knows response.data exists
 *   console.log(response.data.email)
 * }
 */
export function isSuccess<T>(
  response: ApiResponse<T>
): response is { success: true; data: T } {
  return response.success === true
}

/**
 * Type guard to check if response is an error
 * 
 * @example
 * const response = await apiClient<UserData>('/api/users/me')
 * if (isError(response)) {
 *   // TypeScript knows response.error exists
 *   console.error(response.error.code)
 * }
 */
export function isError<T>(
  response: ApiResponse<T>
): response is { success: false; error: { code: string; message: string; details?: unknown } } {
  return response.success === false
}

/**
 * Helper to match on API response with handlers for success and error cases
 * 
 * @example
 * const message = matchResponse(
 *   response,
 *   (data) => `Welcome ${data.name}!`,
 *   (error) => `Error: ${error.message}`
 * )
 */
export function matchResponse<T, S, E>(
  response: ApiResponse<T>,
  onSuccess: (data: T) => S,
  onError: (error: { code: string; message: string; details?: unknown }) => E
): S | E {
  if (response.success) {
    return onSuccess(response.data)
  }
  return onError(response.error)
}

/**
 * React Query-friendly error handler
 * Converts API errors to Error objects for better integration with react-query
 *
 * @example
 * const { data, error } = useQuery({
 *   queryKey: ['user'],
 *   queryFn: () => apiClient('/api/users/me').then(throwOnError)
 * })
 */
export function throwOnError<T>(response: ApiResponse<T>): T {
  return unwrap(response)
}

/**
 * Returns true when the error is a known "Restate still processing" signal —
 * meaning the HTTP layer timed out but the workflow may still complete.
 * Use this to keep the loading spinner instead of showing an error banner.
 */
export function isOnboardingTimeoutError(err: unknown): boolean {
  if (!(err instanceof ApiClientError)) return false
  return (
    err.code === 'request_timeout' ||
    err.code === 'service_unavailable' ||
    err.status === 503
  )
}
